/**
 * NotificationOptIn — Profile-page card that lets the user enable / disable
 * push notifications and send themselves a test ping.
 */
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Send, Check, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from './ui/button';
import {
  pushSupported,
  getPushPermission,
  isSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from '../utils/pushNotifications';

const NotificationOptIn = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupported(pushSupported());
    setPermission(getPushPermission());
    if (pushSupported()) isSubscribed().then(setSubscribed);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush();
      if (r.ok) {
        setSubscribed(true);
        setPermission('granted');
        toast.success("You're in — we'll ping you when new chapters drop.");
      } else if (r.reason === 'denied') {
        toast.error('Permission blocked. Enable notifications in your browser settings.');
      } else if (r.reason === 'unsupported') {
        toast.error("Your browser doesn't support web push.");
      } else {
        toast.error('Could not enable notifications. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success('Notifications turned off.');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      if (r.sent > 0) toast.success('Test sent — check your notifications.');
      else toast.error('Could not send test push. Make sure notifications are enabled.');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div
        className="rounded-xl p-4 bg-slate-900/60 border border-slate-700"
        data-testid="notif-optin-card"
      >
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-gray-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white">Push notifications</p>
            <p className="text-[11px] text-gray-400">
              Your browser doesn't support web push.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-4 bg-gradient-to-br from-indigo-700/30 via-fuchsia-700/15 to-slate-900/40 border border-indigo-500/40"
      data-testid="notif-optin-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-400/40 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-indigo-200" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-white">Push notifications</p>
            {subscribed && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600/30 text-emerald-200 border border-emerald-500/40 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Enabled
              </span>
            )}
            {!subscribed && permission === 'denied' && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600/30 text-red-200 border border-red-500/40 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Blocked
              </span>
            )}
          </div>
          <p className="text-xs text-gray-300 mt-1 leading-relaxed">
            Get a phone-level ping the moment a new chapter or episode drops in
            a series you <span className="text-fuchsia-300 font-semibold">Follow</span> —
            even when the app is closed. Tap the <span className="text-fuchsia-300">Follow</span> button on any series in your Library to opt in.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {!subscribed ? (
              <Button
                onClick={enable}
                disabled={busy || permission === 'denied'}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="notif-enable-btn"
              >
                <Bell className="w-4 h-4 mr-1.5" />
                Enable notifications
              </Button>
            ) : (
              <>
                <Button
                  onClick={test}
                  disabled={busy}
                  variant="outline"
                  className="text-indigo-200 border-indigo-500/40 hover:bg-indigo-500/10"
                  data-testid="notif-test-btn"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  Send test
                </Button>
                <Button
                  onClick={disable}
                  disabled={busy}
                  variant="outline"
                  className="text-red-300 border-red-500/40 hover:bg-red-500/10"
                  data-testid="notif-disable-btn"
                >
                  <BellOff className="w-4 h-4 mr-1.5" />
                  Turn off
                </Button>
              </>
            )}
          </div>

          {permission === 'denied' && (
            <p className="text-[11px] text-red-300/90 mt-2">
              You blocked notifications earlier. Re-enable them in your browser
              settings (lock icon → Site settings → Notifications) and refresh.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationOptIn;
